import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, List, ListItem, Stack } from '@mui/material';
import {
  PageContentLayout,
  PageHeaderLayout,
  Link,
  ListTotal,
  SideMenu,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
  ActionsMenu,
} from 'melp-design/components';
import { APP, ROUTES } from 'config';
import {
  expenseStatuses,
  expenseTypes,
  useExpenses,
  COLOR_BY_STATUS,
  useExportToXls,
  expensesFiltersSchema,
  useExportXlsTemplate,
} from 'store/expenses';
import { shopItemCategories } from 'types/MarketplaceItems';
import moment from 'moment';
import { formatCurrency, formatList } from 'utils/format';
import { useDefaultCurrency, useMe } from 'state/Administrators';
import { DownloadXlsAction } from 'components/actions';
import {
  CompaniesFilter,
  DateRangeFilter,
  FilterButton,
  Filters,
  SourcesFilter,
  SelectFilter,
  OpenSearchFilter,
} from 'components/filters';
import { RECOGNITION_SHOP_ID } from 'store/recognitions';
import { useSearchParams } from 'utils/navigation';
import { useRouteMatch } from 'react-router-dom';
import { useModalContext } from 'store/modal';
import { ExpensesImportModal } from 'containers/expenses';
import { EmployeeCell } from 'components/table';

export const ExpensesList: FC = () => {
  const { t, i18n } = useTranslation();
  const { currency } = useDefaultCurrency();
  const { path } = useRouteMatch();
  const { openModal } = useModalContext();
  const { hasViewPermission, hasEditPermission } = useMe();

  const { searchParams: filters, navigate } = useSearchParams(
    expensesFiltersSchema,
  );

  const { data: expenses, isLoading } = useExpenses(filters);

  const { mutate: exportXlsTemplate, isLoading: isExportingXlsTemplate } =
    useExportXlsTemplate();
  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('expenses.title'),
          },
        ]}
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'expenses',
                label: t('expenses.title'),
                url: ROUTES.expenses.list,
                isActive: path === ROUTES.expenses.list,
              },
            ]}
          />
        }
      >
        <TableWrapper>
          <TableToolbar>
            <Stack gap={1}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                gap={1}
              >
                <ActionsMenu
                  type="spread"
                  items={[
                    {
                      label: t('actions.import'),
                      color: 'primary',
                      disabled: !hasEditPermission(['expenses']),
                      onClick: () => {
                        openModal({
                          component: ExpensesImportModal,
                        });
                      },
                    },
                    {
                      label: t('global.actions.export_template'),
                      disabled:
                        isExportingXlsTemplate ||
                        !hasEditPermission(['expenses']),
                      onClick: () => {
                        exportXlsTemplate();
                      },
                    },
                  ]}
                />
              </Stack>
              <Divider />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <ListTotal total={expenses?.total} />

                <Stack direction="row" alignItems="center" gap={1}>
                  <Filters
                    value={filters}
                    onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                    initialValues={expensesFiltersSchema.parse({})}
                  >
                    {({ value, initialValues, setFilter }) => (
                      <>
                        <FilterButton
                          label={t('expenses.filters.type.label')}
                          value={value.type}
                          initialValue={initialValues.type}
                          onChange={(v) => setFilter('type', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              options={expenseTypes.map((type) => ({
                                key: type,
                                label: t(
                                  `expenses.filters.type.options.${type}`,
                                ),
                              }))}
                              multiple
                              searchable={false}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('employees.company')}
                          value={value.companies}
                          initialValue={initialValues.companies}
                          onChange={(v) => setFilter('companies', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <CompaniesFilter
                              value={value}
                              onApplyFilter={applyFilter}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('marketplaceOrders.orderDate')}
                          value={value.createdAt}
                          initialValue={initialValues.createdAt}
                          onChange={(v) => setFilter('createdAt', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <DateRangeFilter
                              value={value}
                              onApply={applyFilter}
                              onClear={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('expenses.expense_date')}
                          value={value.date}
                          initialValue={initialValues.date}
                          onChange={(v) => setFilter('date', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <DateRangeFilter
                              value={value}
                              onApply={applyFilter}
                              onClear={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('wallet.employeeWalletFundsSource')}
                          value={value.source}
                          initialValue={initialValues.source}
                          onChange={(v) => setFilter('source', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SourcesFilter
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('expenses.filters.categories.label')}
                          value={value.categories}
                          initialValue={initialValues.categories}
                          onChange={(v) => setFilter('categories', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple
                              options={shopItemCategories.map((category) => ({
                                key: category,
                                label: t(
                                  `marketplaceItems.itemCategory-${category}`,
                                ),
                              }))}
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('expenses.filters.status.label')}
                          value={value.status}
                          initialValue={initialValues.status}
                          onChange={(v) => setFilter('status', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple
                              options={expenseStatuses.map((status) => ({
                                key: status,
                                label: t(`expenses.status.${status}`),
                              }))}
                              searchable={false}
                            />
                          )}
                        </FilterButton>

                        {hasViewPermission(['employees']) ? (
                          <OpenSearchFilter
                            value={value.search}
                            onApplyFilter={(v) => setFilter('search', v)}
                            placeholder={t(
                              'expenses.filters.search.placeholder',
                            )}
                          />
                        ) : null}
                      </>
                    )}
                  </Filters>

                  <Divider orientation="vertical" variant="middle" flexItem />

                  <DownloadXlsAction
                    listLength={expenses?.total ?? 0}
                    exportToExcel={() => exportToXls({ filters })}
                    loading={isExportingToXls}
                    limitedRange={
                      filters.date
                        ? {
                            limit: 1,
                            unit: 'year',
                            dateFrom: filters.date?.from,
                            dateTo: filters.date?.to,
                          }
                        : {
                            limit: 1,
                            unit: 'year',
                            dateFrom: filters.createdAt?.from,
                            dateTo: filters.createdAt?.to,
                          }
                    }
                  />
                </Stack>
              </Stack>
            </Stack>
          </TableToolbar>

          <Table
            data={expenses?.items ?? []}
            isLoading={isLoading}
            columns={[
              {
                id: 'employee',
                header: t('entity_type.employee'),
                cell: ({ row: { original: expense } }) => (
                  <EmployeeCell employee={expense.employee} />
                ),
              },
              {
                id: 'date',
                header: t('marketplaceOrders.orderDate'),
                cell: ({ row: { original: expense } }) =>
                  moment(expense.createdAt).format('l'),
              },
              {
                id: 'expenseDate',
                header: t('expenses.expense_date'),
                cell: ({ row: { original: expense } }) =>
                  expense.date ? moment(expense.date).format('l') : '-',
              },
              {
                id: 'source',
                header: t('wallet.employeeWalletFundsSource'),
                cell: ({ row: { original: expense } }) => (
                  <List sx={{ py: 0 }}>
                    {expense.source.map((s) => (
                      <ListItem sx={{ p: 0, mb: 1 }} key={s.id}>
                        {s.id === RECOGNITION_SHOP_ID ? (
                          hasViewPermission(['employeeRecognition']) ? (
                            <Link
                              label={s.name}
                              to={ROUTES.recognitions.settings}
                            />
                          ) : (
                            <Typography>{s.name}</Typography>
                          )
                        ) : hasViewPermission(['benefits']) ? (
                          <Link
                            label={s.name}
                            to={ROUTES.benefits.details.replace(':id', s.id)}
                          />
                        ) : (
                          <Typography>{s.name}</Typography>
                        )}
                      </ListItem>
                    ))}
                  </List>
                ),
              },
              {
                id: 'description',
                header: t('expenses.table.description.title'),
                cell: ({ row: { original: expense } }) => expense.description,
              },
              {
                id: 'categories',
                header: t('expenses.table.categories.title'),
                cell: ({ row: { original: expense } }) =>
                  formatList(
                    i18n.language,
                    expense.categories.map((category) =>
                      t(`marketplaceItems.itemCategory-${category}`),
                    ),
                  ),
              },
              {
                id: 'price',
                header: t('employees.reserved'),
                meta: { align: 'right' },
                cell: ({ row: { original: expense } }) =>
                  formatCurrency(i18n.language, expense.amount, currency),
              },
              {
                id: 'status',
                header: t('expenses.table.status.title'),
                cell: ({ row: { original: expense } }) => (
                  <Typography color={COLOR_BY_STATUS[expense.status]}>
                    {t(`expenses.status.${expense.status}`)}
                  </Typography>
                ),
              },
            ]}
            pagination={
              expenses && {
                count: expenses.total,
                page: expenses.page,
                pageSize: expenses.pageSize,
                pageSizeOptions: APP.pagination.defaults.pageSize,
                onChange: (page, pageSize) => {
                  navigate({ params: { page, pageSize } });
                },
              }
            }
          />
        </TableWrapper>
      </PageContentLayout>
    </Stack>
  );
};
